// Question order for PDF generation - matches the order questions appear across wizard pages
// Pages: Study Design → Efficacy Analysis → Safety Analysis → Standard PK → Comparative PK → Bioavailability → Standard PD → Comparative PD

// Base questions order (common to all study types - from Base.js)
const baseQuestionsOrder = [
  "qStudyPhase",
  "qStudyPhaseNames",
  "qParticipantsName",
  "qComparative",
];

// Multiple Arm study questions order
export const multipleArmQuestionsOrder = [
  ...baseQuestionsOrder,
  // Study Design page
  "qDoseFinding",
  "qTgNames",
  "qTgNamesComparison",
  "qParticipantNumber",
  "qDemPopulation",
  "qCutOffsForDemographicsTables",
  // Efficacy Analysis page
  "qIsEfficacy",
  "qEfficacyAnalytes",
  "qPrimaryEfficacyComparisonNames",
  "qEfficacyPopulations",
  "qEfficacyAnalysisTypeNames",
  "qEfficacyTimepointNames",
  "qPrimaryEfficacy",
  "qPrimaryEfficacyAnalytes",
  "qPrimaryEfficacyPopulation",
  "qPrimaryEfficacyAnalysisTypeNames",
  "qPrimaryEfficacyAnalytesComparison",
  "qPrimaryEfficacyTimepointNames",
  "qEfficacyKaplanMeierAnalytes",
  // Safety Analysis page
  "qSafetyBaseline",
  "qSafetyEndpoint",
  "qCutOffsForAbnormalLabTables",
  "qCutOffs",
  "qCutOffsForAdverseEventsTables",
  // Standard PK page
  "qPkStdEnabled",
  // Standard PK Analysis blocks are handled separately
  // Comparative PK page
  "qPkCompEnabled",
  // Comparative PK Analysis blocks are handled separately
  // Bioavailability page
  "qPkRelBio",
  // Relative Bioavailability Analysis blocks are handled separately
  // Standard PD page
  "qPdStdEnabled",
  // Standard PD Analysis blocks are handled separately
  // Comparative PD page
  "qPdCompEnabled",
  // Comparative PD Analysis blocks are handled separately
];

// Dose Finding study questions order
export const doseFindingQuestionsOrder = [
  ...baseQuestionsOrder,
  // Study Design page
  "qDoseFinding",
  "qDoseFindingSafety",
  "qTgNamesDoseFinding",
  "qTgNames",
  "qParticipantNumber",
  "qDemPopulation",
  "qCutOffsForDemographicsTables",
  // Efficacy Analysis page
  "qIsEfficacy",
  "qEfficacyAnalytes",
  "qPrimaryEfficacyComparisonNames",
  "qEfficacyPopulations",
  "qEfficacyAnalysisTypeNames",
  "qEfficacyTimepointNames",
  "qPrimaryEfficacy",
  "qPrimaryEfficacyAnalytes",
  "qPrimaryEfficacyPopulation",
  "qPrimaryEfficacyAnalysisTypeNames",
  "qPrimaryEfficacyAnalytesComparison",
  "qPrimaryEfficacyTimepointNames",
  "qEfficacyKaplanMeierAnalytes",
  // Safety Analysis page
  "qSafetyBaseline",
  "qSafetyEndpoint",
  "qCutOffsForAbnormalLabTables",
  "qCutOffs",
  "qCutOffsForAdverseEventsTables",
  // Standard PK page
  "qPkStdEnabled",
  // Standard PK Analysis blocks are handled separately
  // Comparative PK page
  "qPkCompEnabled",
  // Comparative PK Analysis blocks are handled separately
  // Bioavailability page
  "qPkRelBio",
  // Relative Bioavailability Analysis blocks are handled separately
  // Standard PD page
  "qPdStdEnabled",
  // Standard PD Analysis blocks are handled separately
  // Comparative PD page
  "qPdCompEnabled",
  // Comparative PD Analysis blocks are handled separately
];

// Single Arm study questions order
export const singleArmQuestionsOrder = [
  ...baseQuestionsOrder,
  // Study Design page
  "qSingleArmMultipleColumns",
  "qDemPopulation",
  "qCutOffsForDemographicsTables",
  // Efficacy Analysis page
  "qIsEfficacy",
  "qEfficacyAnalytes",
  "qEfficacyPopulations",
  "qEfficacyAnalysisTypeNames",
  "qEfficacyTimepointNames",
  "qPrimaryEfficacy",
  "qPrimaryEfficacyAnalytes",
  "qPrimaryEfficacyPopulation",
  "qPrimaryEfficacyAnalysisTypeNames",
  "qPrimaryEfficacyAnalytesComparison",
  "qPrimaryEfficacyTimepointNames",
  "qEfficacyKaplanMeierAnalytes",
  // Safety Analysis page
  "qSafetyBaseline",
  "qSafetyEndpoint",
  "qCutOffsForAbnormalLabTables",
  "qCutOffs",
  "qCutOffsForAdverseEventsTables",
  // Standard PK page
  "qPkStdEnabled",
  // Standard PK Analysis blocks are handled separately
  // Comparative PK page
  "qPkCompEnabled",
  // Comparative PK Analysis blocks are handled separately
  // Bioavailability page
  "qPkRelBio",
  // Relative Bioavailability Analysis blocks are handled separately
  // Standard PD page
  "qPdStdEnabled",
  // Standard PD Analysis blocks are handled separately
  // Comparative PD page
  "qPdCompEnabled",
  // Comparative PD Analysis blocks are handled separately
];

// Standard PK Analysis block questions order (matches StandardPKQuestions.js)
export const standardPkBlockQuestionsOrder = [
  "qPkTable",
  "qPkPopulation",
  "qPkMultiDose",
  "qPkFigures",
  "qPkFiguresDescription",
  "qPkFiguresTimepoints",
  "qPkFiguresExtra",
  "qPkStdColumn",
  "qPkTimelines",
  "qPkStatsAnalysis",
];

// Comparative PK Analysis block questions order
// Note: Uses base field names (not "Comp"-suffixed) because the saved block data
// in qPkCompAnalysis stores fields with base names (qPkTable, qPkPopulation, etc.)
export const comparativePkBlockQuestionsOrder = [
  "qPkTable",
  "qPkPopulation",
  "qPkMultiDose",
  "qPkComparisonName",
  "qPkFigures",
  "qPkFiguresExtra",
  "qPkAnalytes",
  "qPkComparisonPairs",
  "qPkExposure",
  "qPkDoseInteraction",
  "qPkTimelines",
  "qPkStatsAnalysis",
];

// Relative Bioavailability PK Analysis block questions order (matches BioavailabilityQuestions.js)
export const relBioPkBlockQuestionsOrder = [
  "qPkRelBioTable",
  "qPkRelBioComparisonPair",
  "qPkRelBioTimelines",
  "qPkRelBioEstimateRange",
];

// Standard PD Analysis block questions order (matches StandardPDQuestions.js)
export const standardPdBlockQuestionsOrder = [
  "qPdTable",
  "qPdPopulation",
  "qPdColumnHeaders",
  "qPdTimepoints",
];

// Comparative PD Analysis block questions order
// Note: Uses base field names (not "Comp"-suffixed) because the saved block data
// in qPdCompAnalyses stores fields with base names (qPdTable, qPdPopulation, etc.)
export const comparativePdBlockQuestionsOrder = [
  "qPdTable",
  "qPdPopulation",
  "qPdCompName",
  "qPdSummaryType",
  "qPdParameters",
  "qPdTreatmentGroups",
  "qPdComparisonPairs",
  "qPdDoseInteraction",
  "qPdTimepoints",
  "qPdStatTables",
];
